import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

// Simplified toast implementation
const ToastContext = React.createContext({
  toasts: [],
  toast: () => {}
});

export function Toast({
  className,
  variant = "default",
  children,
  ...props
}) {
  return (
    <div
      className={cn(
        "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg",
        variant === "destructive" ? "border-destructive bg-destructive text-destructive-foreground" : "border bg-background",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = React.useState([]);

  return (
    <ToastContext.Provider value={{ toasts, toast: () => {} }}>
      {children}
    </ToastContext.Provider>
  );
}

export function ToastViewport({ className, ...props }) {
  return (
    <div
      className={cn(
        "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
        className
      )}
      {...props}
    />
  );
}

export function ToastTitle({ className, ...props }) {
  return <div className={cn("text-sm font-semibold", className)} {...props} />;
}

export function ToastDescription({ className, ...props }) {
  return <div className={cn("text-sm opacity-90", className)} {...props} />;
}

export function ToastClose({ className, ...props }) {
  return (
    <button
      className={cn(
        "absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100",
        className
      )}
      type="button"
      {...props}
    >
      <X className="h-4 w-4" />
    </button>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);
  
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  
  return context;
}

export function Toaster() {
  return (
    <ToastProvider>
      <ToastViewport />
    </ToastProvider>
  );
}
